import { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Package, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shipment, TrackingResult, formatCurrency, getStatusColor, getStatusLabel } from '@/lib/index';
import { sampleShipments } from '@/data/index';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/lib/language';

interface TrackingCardProps {
  className?: string;
}

export function TrackingCard({
  className
}: TrackingCardProps) {
  const { t } = useLanguage();
  const [trackingNumber, setTrackingNumber] = useState('');
  const [result, setResult] = useState<TrackingResult | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!trackingNumber.trim()) return;

    setIsSearching(true);
    await new Promise(resolve => setTimeout(resolve, 800));

    const foundShipment = sampleShipments.find(ship => 
      ship.trackingNumber.toLowerCase() === trackingNumber.trim().toLowerCase()
    );

    if (foundShipment) {
      setResult({
        found: true,
        shipment: foundShipment
      });
    } else {
      setResult({
        found: false,
        error: t('tracking.notFound')
      });
    }

    setIsSearching(false);
  };

  return (
    <Card className={cn('w-full', className)}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="h-5 w-5 text-primary" />
          {t('tracking.title')}
        </CardTitle>
        <CardDescription>
          {t('tracking.subtitle')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <form onSubmit={handleSearch} className="flex gap-2">
          <Input
            type="text"
            placeholder={t('tracking.placeholder')}
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
            className="flex-1"
            disabled={isSearching}
          />
          <Button type="submit" disabled={isSearching || !trackingNumber.trim()}>
            <Search className="h-4 w-4 mr-2" />
            {isSearching ? t('common.loading') : t('tracking.searchButton')}
          </Button>
        </form>

        {result && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            {result.found && result.shipment ? (
              <div className="p-4 border rounded-lg bg-muted/50">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-semibold">
                    {t('tracking.orderNumber')}: {result.shipment.trackingNumber}
                  </div>
                  <Badge className={getStatusColor(result.shipment.status)}>
                    {getStatusLabel(result.shipment.status)}
                  </Badge>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-muted-foreground">{t('tracking.recipient')}:</div>
                    <div className="font-medium">{result.shipment.recipient.name}</div>
                    <div className="text-xs text-muted-foreground">{result.shipment.recipient.address}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">{t('tracking.amount')}:</div>
                    <div className="font-medium">{formatCurrency(result.shipment.amount.total)} {result.shipment.amount.currency}</div>
                  </div>
                </div>
              </div>
            ) : (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {result.error}
                </AlertDescription>
              </Alert>
            )}
          </motion.div>
        )}
      </CardContent>
    </Card>
  );
}